import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { personService } from '@shared/services/personService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export const useEducationForm = (person) => {
    const { t } = useTranslation();
    const [educationData, setEducationData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingEducation, setEditingEducation] = useState(null);
    const [degreeOptions, setDegreeOptions] = useState([]);
    const [institutionOptions, setInstitutionOptions] = useState([]);

  

    const educationValidationSchema = yup.object({
        degreeLevelId: yup.string()
            .required(t('validation.person.education.degreeRequired')),
        hrEducationInstId: yup.string()
            .required(t('validation.person.education.institutionRequired')),
        graduationDate: yup.string()
            .required(t('validation.person.education.graduationDateRequired')),
        note: yup.string()
            .max(200, t('validation.person.education.notesLength'))
    });

    const {
        register: educationFormRegister,
        formState: { errors: educationErrors },
        setValue: setEducationValue,
        handleSubmit,
        reset,
        watch
    } = useForm({
        resolver: yupResolver(educationValidationSchema),
        defaultValues: {
            degreeLevelId: '',
            hrEducationInstId: '',
            specialty: '',
            docNo: '',
            docDate: '',
            graduationDate: '',
            note: ''
        }
    });

    useEffect(() => {
        const fetchDegreeOptions = async () => {
            try {
                const data = await personService.getDegreeLevels();
                const options = data.map(degree => ({
                    value: degree.id,
                    label: degree.education + ' - ' + degree.degreeLevel
                }));
                setDegreeOptions(options);
            } catch (error) {
                console.error('Error fetching degree options:', error);
                setDegreeOptions([]);
            }
        };

        fetchDegreeOptions();
    }, []); 

    useEffect(() => {
        const fetchInstitutionOptions = async () => {
            try {
                const data = await personService.getHrEducationalInstitutions();
                const options = data.map(institution => ({
                    value: institution.id,
                    label: institution.name
                }));        
                setInstitutionOptions(options);
            } catch (error) {
                console.error('Error fetching institution options:', error);
                setInstitutionOptions([]);
            }
        };
        fetchInstitutionOptions();
    }, []);

    const fetchEducation = async () => {
        try {
            if (person?.id) {
                const data = await personService.getEducations(person.id);
                setEducationData(data || []);
            }
        } catch (error) {   
            console.error('Error fetching education:', error);
            notifyError(t('persons.education.fetchError'));
            setEducationData([]);
        }
    };

    useEffect(() => {
        if (person?.id) {
            fetchEducation();
        }
    }, [person?.id]);

    const submitEducationForm = async (formData) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.education.personIdRequired'));
                return false;
            }

            // Format dates to YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const d = new Date(date);
                return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
            };

            const payload = {
                graduationDate: formatDate(formData.graduationDate),
                hrEducationInstId: formData.hrEducationInstId,
                degreeLevelId: formData.degreeLevelId,
                personId: person.id.toString(),
                specialty: formData.specialty,
                docNo: formData.docNo,
                docDate: formatDate(formData.docDate),
                note: formData.note
            };

            if (isEditing && editingEducation) {
                await personService.updateEducation(editingEducation.id, payload);
                notifySuccess(t('persons.education.updateSuccess'));
            } else {
                await personService.addEducation(payload);
                notifySuccess(t('persons.education.addSuccess'));
            }

            reset({
                degreeLevelId: '',
                hrEducationInstId: '',
                specialty: '',
                docNo: '',
                docDate: '',
                graduationDate: '',
                note: ''
            });
            setIsEditing(false);
            setEditingEducation(null);
            await fetchEducation();
            return true;
        } catch (error) {
            console.error('Education operation failed:', error);
            notifyError(isEditing 
                ? t('persons.education.updateError')
                : t('persons.education.addError')
            );
            return false;
        }
    };

    const handleEdit = (education) => {
        setIsEditing(true);
        setEditingEducation(education);
        
        const degreeId = degreeOptions.find(opt => opt.label === education.degreeLevelDegreeLevel)?.value;
        const institutionId = institutionOptions.find(opt => opt.label === education.hrEducationInstName)?.value;

        setEducationValue('degreeLevelId', degreeId || '');
        setEducationValue('hrEducationInstId', institutionId || '');
        setEducationValue('specialty', education.specialty || '');
        setEducationValue('docNo', education.docNo || '');
        setEducationValue('docDate', education.docDate || '');
        setEducationValue('graduationDate', education.graduationDate || '');
        setEducationValue('note', education.note || '');

       
    };

    const handleDelete = async (educationId) => {
        try {
            await personService.deleteEducation(educationId);
            notifySuccess(t('common.deletedSuccessfully'));
            await fetchEducation();
        } catch (error) {
            notifyError(t('common.errorMessage'));
        }
    };

    const handleCancel = () => {
        reset({
            degreeLevelId: '',
            hrEducationInstId: '',
            specialty: '',
            docNo: '',
            docDate: '',
            graduationDate: '',
            note: ''
        });
        setIsEditing(false);
        setEditingEducation(null);
    };

    return {
        educationData,
        educationFormRegister,
        educationErrors,
        handleEdit,
        handleDelete,
        submitEducationForm,
        isEditing,
        handleSubmit,
        handleCancel,
        degreeOptions,
        institutionOptions,
        watch,
        setValue: setEducationValue
    };
}; 